import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signOut
} from '../../../auth/clientAuthApi';
import { auth } from '../../../auth/clientAppAuth';
import {
  clearPortalClientSession,
  persistPortalClientSession,
  resolveStorefrontClient
} from '../utils/storefrontClientSync';

const StorefrontAuthContext = createContext(null);

const COMPANY_ACCOUNT_ERROR = 'Company accounts cannot use storefront client sign-in.';
const MISSING_ACCESS_ERROR = 'This account is not linked to this client portal yet. You can still book as a guest.';

export const StorefrontAuthProvider = ({ companyData, children }) => {
  const [storefrontClient, setStorefrontClient] = useState(null);
  const [sessionUser, setSessionUser] = useState(null);
  const [hasPortalAccess, setHasPortalAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authNotice, setAuthNotice] = useState('');

  const refreshStorefrontAuth = useCallback(async (sessionUser = auth.currentUser, options = {}) => {
    const silent = Boolean(options?.silent);

    if (!companyData?.id) {
      setStorefrontClient(null);
      setSessionUser(null);
      setHasPortalAccess(false);
      setAuthNotice('');
      if (!silent) setLoading(false);
      return null;
    }

    if (!sessionUser) {
      setStorefrontClient(null);
      setSessionUser(null);
      setHasPortalAccess(false);
      setAuthNotice('');
      if (!silent) setLoading(false);
      return null;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      const resolution = await resolveStorefrontClient(sessionUser, companyData);

      setSessionUser(resolution.sessionUser);
      setStorefrontClient(resolution.storefrontClient || null);
      setHasPortalAccess(Boolean(resolution.hasPortalAccess));

      if (resolution.storefrontClient) {
        persistPortalClientSession(resolution.storefrontClient);
      }

      if (resolution.reason === 'company-account') {
        setAuthNotice(COMPANY_ACCOUNT_ERROR);
      } else if (resolution.reason === 'missing-company-access') {
        setAuthNotice(MISSING_ACCESS_ERROR);
      } else {
        setAuthNotice('');
      }

      return resolution;
    } catch (error) {
      console.error('Error resolving storefront auth:', error);
      setStorefrontClient(null);
      setHasPortalAccess(false);
      setAuthNotice('We could not verify this account right now. Please try again.');
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [companyData]);

  const patchStorefrontClient = useCallback((patch) => {
    setStorefrontClient((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      if (patch.avatarUrl !== undefined) {
        localStorage.setItem('clientAvatarUrl', patch.avatarUrl || '');
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setLoading(true);

    const unsubscribe = auth.onAuthStateChanged((sessionUser) => {
      refreshStorefrontAuth(sessionUser);
    });

    return () => unsubscribe();
  }, [refreshStorefrontAuth]);

  const setRememberMeState = (rememberMe, email = '') => {
    localStorage.setItem('clientPortalRememberMe', rememberMe ? 'true' : 'false');

    if (rememberMe && email) {
      localStorage.setItem('clientPortalEmail', email);
    } else if (!rememberMe) {
      localStorage.removeItem('clientPortalEmail');
    }
  };

  const signInWithStorefrontEmail = useCallback(async ({ email, password, rememberMe }) => {
    if (!companyData?.id) {
      throw new Error('Storefront company is not available yet.');
    }

    await setPersistence(
      auth,
      rememberMe ? browserLocalPersistence : browserSessionPersistence
    );

    setRememberMeState(rememberMe, email);
    const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
    const resolution = await refreshStorefrontAuth(userCredential.user);

    if (resolution?.reason === 'company-account') {
      await signOut(auth);
      clearPortalClientSession();
      throw new Error(COMPANY_ACCOUNT_ERROR);
    }

    if (resolution?.reason === 'missing-company-access') {
      await signOut(auth);
      clearPortalClientSession();
      throw new Error(MISSING_ACCESS_ERROR);
    }

    if (!resolution) {
      throw new Error('We could not finish verifying your storefront session. Please try again.');
    }

    return resolution?.storefrontClient || null;
  }, [companyData, refreshStorefrontAuth]);

  const signOutStorefront = useCallback(async () => {
    clearPortalClientSession();
    await signOut(auth);
    setStorefrontClient(null);
    setSessionUser(null);
    setHasPortalAccess(false);
    setAuthNotice('');
  }, []);

  const value = useMemo(() => ({
    companyData,
    storefrontClient,
    sessionUser,
    hasPortalAccess,
    isAuthenticated: Boolean(sessionUser),
    loading,
    authNotice,
    refreshStorefrontAuth,
    patchStorefrontClient,
    signInWithStorefrontEmail,
    signOutStorefront
  }), [
    companyData,
    storefrontClient,
    sessionUser,
    hasPortalAccess,
    loading,
    authNotice,
    refreshStorefrontAuth,
    patchStorefrontClient,
    signInWithStorefrontEmail,
    signOutStorefront
  ]);

  return (
    <StorefrontAuthContext.Provider value={value}>
      {children}
    </StorefrontAuthContext.Provider>
  );
};

export const useStorefrontAuth = () => {
  const context = useContext(StorefrontAuthContext);

  if (!context) {
    throw new Error('useStorefrontAuth must be used within a StorefrontAuthProvider');
  }

  return context;
};
